import { GATEWAY_SERVED_BY } from '@web-platform/shared-universal-constants/src/conductorHeaders';
import {
  MUTATION_RESPONSE_RECEIVED,
  MUTATION_REQUIRED,
} from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  ALTERNATIVE_DATES_SESSION,
  RADAR_CREATE_SESSIONS,
  SONAR_V3_CREATE_SESSION,
  SONAR_V3_ALPHA_CREATE_SESSION,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isAcornRequest } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { isSonarV3ErrorPayload } from '@web-platform/shared-universal-utils-frontend/src/helpers/isSonarErrorPayload';
import { isFlightsResponseError } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';

import { getKey } from '../../../shared/network/getKey';

import type {
  Action,
  MutationRequiredAction,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  SessionState,
  SessionStateValue,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/session';
import type { RadarResponseSuccess } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';

export type {
  SessionState,
  SessionStateValue,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/session';
export const INITIAL_STATE: SessionState = {};

const getFlightsSessionId = (response: RadarResponseSuccess): string => {
  if (!isAcornRequest() && isFlightsResponseError(response)) return '';
  return response?.body?.itineraries?.context?.sessionId ?? '';
};

const getSessionId = (response: RadarResponseSuccess): string => {
  const { context } = response.body;
  if (!context) {
    return '';
  }

  const { sessionId } = context;
  if (!sessionId) {
    return '';
  }

  return sessionId;
};

const shouldSkipBananaRadarSession = (response: RadarResponseSuccess): boolean => {
  if (isAcornRequest()) return false;
  // Hotels now handled by Michelin, not Radar - only check flights
  return isFlightsResponseError(response);
};

function mutationRequired(state: SessionState, action: MutationRequiredAction): SessionState {
  switch (action.payload.type) {
    case RADAR_CREATE_SESSIONS: {
      return {
        ...state,
        [RADAR_CREATE_SESSIONS]: {
          mutationKey: getKey(action.payload.type, action.payload.variables),
          sessionViewGuid:
            typeof action.payload.variables.viewId === 'string'
              ? action.payload.variables.viewId
              : '',
        },
      };
    }

    default:
      return state;
  }
}
// @ts-expect-error: Parameter 'action' implicitly has an 'any' type.ts(7006)
function mutationReceived(state: SessionState = {}, action): SessionState {
  switch (action.payload.type) {
    case RADAR_CREATE_SESSIONS: {
      const { payload } = action;
      if (payload.response.error) return state;
      if (shouldSkipBananaRadarSession(payload.response)) {
        return state;
      }

      const gatewayRegion = payload.response.headers[GATEWAY_SERVED_BY];

      return {
        ...state,
        [RADAR_CREATE_SESSIONS]: {
          ...state[RADAR_CREATE_SESSIONS],
          sessionId: getSessionId(payload.response),
          flightsSessionId: getFlightsSessionId(payload.response),
          gatewayRegion,
          sessionStart: payload.now,
        },
      };
    }

    case SONAR_V3_ALPHA_CREATE_SESSION:
    case SONAR_V3_CREATE_SESSION: {
      if (isSonarV3ErrorPayload(action.payload.response)) {
        return state;
      }

      return {
        ...state,
        [SONAR_V3_CREATE_SESSION]: {
          ...state[SONAR_V3_CREATE_SESSION],
          flightsSessionId: action.payload.response.body.bookingSessionId,
        },
      };
    }
    case ALTERNATIVE_DATES_SESSION:
      if (action.payload.response.error) {
        return state;
      }

      return {
        ...state,
        [ALTERNATIVE_DATES_SESSION]: {
          sessionId: action.payload.response.body?.pollingSession?.pollingSessionId,
          sessionStart: action.payload.now,
        },
      };
    default:
      return state;
  }
}
export default function sessionReducer(
  state: SessionState = INITIAL_STATE,
  action: Action,
): SessionState {
  if ('error' in action && action.error) {
    return state;
  }

  switch (action.type) {
    case MUTATION_REQUIRED:
      return isAcornRequest() ? state : mutationRequired(state, action);
    case MUTATION_RESPONSE_RECEIVED:
      return mutationReceived(state, action);

    case SEARCH_PARAMS_CHANGED:
      return INITIAL_STATE;

    default:
      return state;
  }
}
const EMPTY_SESSION: SessionStateValue = {};

export const get = <TKey extends keyof SessionState>(
  state: SessionState,
  key: TKey,
): NonNullable<SessionState[TKey]> => state[key] ?? EMPTY_SESSION;
